import { FieldWrapper, FlexBox, FormField, Text } from '@dixa/lib-shared-habla';
import { typography } from '@dixa/lib-shared-tokens';
import { Field } from 'redux-form';

import { FieldToggleBoxHead, ToggleBox, ToggleBoxBody } from 'fable/toggleBox';

import type { SettingsProps } from '../../types/settings';
import { FieldTimeInput } from '../fieldTimeInput/fieldTimeInput';

export enum WaitingTimeSettingType {
  LastAgent = 'last',
  PreferredAgent = 'preferred',
}

const settingsByType: Record<
  WaitingTimeSettingType,
  {
    timeoutKey: string;
    offlineKey: string;
  }
> = {
  [WaitingTimeSettingType.LastAgent]: {
    timeoutKey: 'do_not_offer_timeouts',
    offlineKey: 'personal_agent_offline_timeout',
  },
  [WaitingTimeSettingType.PreferredAgent]: {
    timeoutKey: 'preferred_agent_timeouts',
    offlineKey: 'preferred_agent_offline_timeout',
  },
};

type Props = SettingsProps & {
  type: WaitingTimeSettingType;
};

export function WaitingTimeSettings({ formValues, type }: Props): JSX.Element {
  const { timeoutKey, offlineKey } = settingsByType[type];

  return (
    <>
      <FormField>
        <FlexBox flexDirection="column" gap="100">
          <Text.Heading variant="heading-3" color={typography.color.secondary}>
            Waiting time
          </Text.Heading>

          <Text.Paragraph subdued>
            Decide for how long a conversation should wait for the {type} agent,
            before it will be offered to the next agent in the queue.
          </Text.Paragraph>
        </FlexBox>
      </FormField>

      <FormField>
        <Field
          name={`${timeoutKey}.pstn_phone`}
          component={FieldTimeInput}
          label="Phone, Voicemail & Callbacks"
          unit={{
            name: `${timeoutKey}.pstn_phone_unit`,
          }}
        />
      </FormField>

      <FormField>
        <Field
          name={`${timeoutKey}.email`}
          component={FieldTimeInput}
          label="Email & Contact form"
          unit={{
            name: `${timeoutKey}.email_unit`,
          }}
        />
      </FormField>

      <FormField>
        <Field
          name={`${timeoutKey}.widget_chat`}
          component={FieldTimeInput}
          label="Chat & Dixa Messenger"
          unit={{
            name: `${timeoutKey}.widget_chat_unit`,
          }}
        />
      </FormField>

      <FormField>
        <Field
          name={`${timeoutKey}.facebook_messenger`}
          component={FieldTimeInput}
          label="Texts & Social Media"
          unit={{
            name: `${timeoutKey}.facebook_messenger_unit`,
          }}
        />
      </FormField>

      <FormField>
        <FieldWrapper
          label={`If the ${type} agent is offline`}
          helpIcon={{
            dark: true,
            text:
              type === WaitingTimeSettingType.LastAgent
                ? 'You can offer ongoing conversations and follow-up conversations to all agents in the queue, when the last agent has been offline longer than the defined period.'
                : 'You can offer preferred agent conversations to all agents in the queue, when the preferred agent has been offline longer than the defined period.',
          }}
        >
          <ToggleBox
            headComponent={FieldToggleBoxHead}
            isOpen={formValues[`${offlineKey}_enabled`]}
            textProps={{ variant: '2' }}
            title="Offer to next available agent after timeout"
            input={{
              id: `${offlineKey}_enabled`,
              value: `${offlineKey}_enabled`,
              name: `${offlineKey}_enabled`,
            }}
          >
            <ToggleBoxBody>
              <Field
                name={offlineKey}
                component={FieldTimeInput}
                label={`Timeout for ${type} agent`}
                unit={{ name: `${offlineKey}_unit` }}
              />
            </ToggleBoxBody>
          </ToggleBox>
        </FieldWrapper>
      </FormField>
    </>
  );
}
